// @flow
import * as React from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {colors} from '../../../../../assets/styles/colors';
import {MuiThemeProvider, createTheme} from '@material-ui/core/styles';
import {DateRangeOutlined} from '@material-ui/icons';
import {KeyboardDatePicker} from '@material-ui/pickers';

import type {KeyboardDatePickerProps} from '@material-ui/pickers';
type Props = {
  className?: string,
  label?: React.Node,
  InputProps?: Props,
  position?: string,
  value?: Date | null,
  handleChange: () => void,
} & KeyboardDatePickerProps;

const useStyles = makeStyles(theme => ({
  root: {
    overflow: 'inherit',
    borderRadius: '8px',
    border: `1.25px solid ${colors.grey300}`,
    boxSizing: 'border-box',
    backgroundColor: 'white !important',
    color: `${colors.grey800} !important`,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
  },
  input: {padding: '10px 8px'},
  focused: {},
  textField: {},
  textInputLabel: {
    color: colors.grey600,
  },
  textInput: {
    borderRadius: '8px',
    border: `1.25px solid ${colors.grey300}`,
    boxSizing: 'border-box',
    backgroundColor: 'white !important',
    color: colors.grey800,
    flexWrap: 'wrap',
    '& .MuiFilledInput-input': {
      padding: '15px',
    },
    '& .MuiInputBase-input.Mui-disabled': {
      backgroundColor: colors.grey200,
    },
    '&.Mui-error': {
      border: `1px solid #f44336`,
    },
    '&.Mui-focused': {
      border: `1px solid ${colors.grey500} `,
      backgroundColor: 'white ',
      color: `${colors.grey800} `,
    },
  },
  iconButton: {
    color: `${colors.purple} `,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  datePickerNoLabel: {
    '& button': {
      marginBottom: '16px',
    },

    '& input': {
      paddingBlock: '18px',
    },
  },
}));

const theme = createTheme({
  palette: {
    background: {
      default: '#FFFFFF',
    },
    primary: {
      main: colors.purple3,
    },
  },
  overrides: {
    MuiInputBase: {
      root: {
        backgroundColor: 'white !important',
        border: `1px solid ${colors.grey300} `,

        '&$error': {
          border: '1.25px solid #f44336 !important',
        },
        '&$focused': {
          border: `1px solid ${colors.grey500} `,
          backgroundColor: 'white',
          color: `${colors.grey800} `,
        },
        '&$disabled': {
          backgroundColor: `${colors.grey200} !important`,
        },
      },
      input: {
        color: `${colors.grey800} `,
      },
    },
    MuiFormLabel: {
      root: {
        lineHeight: '0.3',
        color: `${colors.grey600} !important`,
        '&$error': {
          color: '#f44336 !important',
        },
      },
    },
  },
  typography: {
    fontFamily: '"Poppins", "Muli", sans-serif',
  },
});

export default function CustomDatePicker(props: Props) {
  const {
    label,
    className = '',
    value,
    handleChange,
    InputProps,
    position = 'start',
    ...otherProps
  } = props;

  const classes = useStyles();

  return (
    <MuiThemeProvider theme={theme}>
      <KeyboardDatePicker
        autoOk
        value={value}
        label={label}
        className={`${className} ${!label ? classes.datePickerNoLabel : ''}`}
        inputVariant="filled"
        format="DD/MM/YYYY"
        coloricon={colors.purple}
        InputAdornmentProps={{position: position}}
        InputProps={{
          ...InputProps,
          className: classes.root,
          disableUnderline: true,
        }}
        KeyboardButtonProps={{
          'aria-label': 'change date',
        }}
        keyboardIcon={
          <div size="small" className={classes.iconButton}>
            <DateRangeOutlined />
          </div>
        }
        invalidDateMessage={'Formato de fecha inválida'}
        onChange={handleChange}
        {...otherProps}
      />
    </MuiThemeProvider>
  );
}

CustomDatePicker.propTypes = {
  label: PropTypes.node,
  className: PropTypes.string,
  InputProps: PropTypes.object,
  position: PropTypes.string,
  value: PropTypes.object,
  handleChange: PropTypes.func,
};
